// stores/consentStore.js
// Manages the user's cookie / tracking consent (UK PECR + EU ePrivacy).
// Non-essential scripts must NOT run until the user explicitly opts in.
import { defineStore } from 'pinia';
import { disableMetaPixel, enableMetaPixel } from '@/utils/metaPixel';
import { disableXPixel, enableXPixel } from '@/utils/xPixel';
import { updateGtagConsent } from '@/utils/gtag';
import { hasSecretInPath } from '@/utils/secretRoutes';

const STORAGE_KEY = 'cookieConsent';
// Bump this when the cookie categories / policy materially change so users
// are re-asked for consent.
const CONSENT_VERSION = 1;

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export const useConsentStore = defineStore('consent', {
  state: () => {
    const stored = loadStored();
    return {
      // null => user has not decided yet (show banner)
      decided: !!stored,
      necessary: true, // always on, cannot be disabled
      analytics: stored ? !!stored.analytics : false,
      marketing: stored ? !!stored.marketing : false,
      timestamp: stored ? stored.timestamp : null,
      // Controls whether the preferences panel is open in the banner
      showPreferences: false,
      // Marketing tags could not be loaded yet because the URL held a secret
      marketingDeferred: false,
    };
  },
  getters: {
    // Whether the banner should be displayed
    needsDecision: (state) => !state.decided,
  },
  actions: {
    persist() {
      const payload = {
        version: CONSENT_VERSION,
        necessary: true,
        analytics: this.analytics,
        marketing: this.marketing,
        timestamp: Date.now(),
      };
      this.timestamp = payload.timestamp;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (_) {
        // storage may be unavailable (private mode) — fail silently
      }
      this.applyConsent();
    },
    acceptAll() {
      this.analytics = true;
      this.marketing = true;
      this.decided = true;
      this.showPreferences = false;
      this.persist();
    },
    rejectNonEssential() {
      this.analytics = false;
      this.marketing = false;
      this.decided = true;
      this.showPreferences = false;
      this.persist();
    },
    savePreferences({ analytics = false, marketing = false } = {}) {
      this.analytics = analytics;
      this.marketing = marketing;
      this.decided = true;
      this.showPreferences = false;
      this.persist();
    },
    // Re-open the banner so users can change their choice (e.g. footer link)
    reopen() {
      this.showPreferences = true;
      this.decided = false;
    },
    // Apply the current consent state to the page: inject or remove tags.
    applyConsent() {
      if (typeof document === 'undefined') return;
      // Update Google Consent Mode signals (gtag stays loaded; only the
      // consent state changes between 'granted' and 'denied').
      updateGtagConsent({
        analytics: this.analytics,
        marketing: this.marketing,
      });
      if (this.marketing) {
        // Third-party scripts see the current URL as soon as they load, so
        // on secret URLs defer until the next route (see router.afterEach).
        this.marketingDeferred = hasSecretInPath();
        if (this.marketingDeferred) return;
        enableMetaPixel();
        enableXPixel();
      } else {
        this.marketingDeferred = false;
        disableMetaPixel();
        disableXPixel();
      }
    },
    // Called once on app startup to honour a previously-saved choice.
    init() {
      if (this.decided) {
        this.applyConsent();
      }
    },
  },
});
